/**
 * Preparation-request task list — the two-level (task / sub-task) model.
 *
 * Storage (CLAUDE.md §25.2, unchanged table, NO migration): tasks are a
 * JSONB array on the preparation_requests row. Item shape:
 *
 *   { id: 'uuid', text: '…', completed: false, parent: null | 'uuid' }
 *
 *   - `id`        client-generated, stable; assigned before any edit
 *   - `parent`    the parent item's id for a sub-task, null for a top-level
 *                 task. A MISSING `parent` on an older row reads as null.
 *   - every other field an older row carries is preserved untouched.
 *
 * ORDERING INVARIANT — the array is the ONLY source of order: a sub-task
 * sits directly after its parent, and a parent's sub-tasks are contiguous.
 * Numbering (1., 2. / 1.a, 1.b …) is derived from that order at render
 * time and never stored. Depth is capped at two levels.
 *
 * Two layers keep the invariant:
 *   - the editor operations below (insert / demote / promote / move /
 *     remove) each move the item within the array so the invariant holds
 *     after the operation on its own;
 *   - `normalisePrepTasks` is the SAFETY NET, called on every save path
 *     (form submission, amendment save, completion writes): it assigns
 *     missing ids, treats an unknown parent as null, collapses a sub-task
 *     whose parent is itself a sub-task onto the grandparent (console
 *     debug only), and re-orders the array into parent-then-children form.
 *
 * Read side is tolerant: `resolveParents` / `numberPrepTasks` render any
 * older row or prior_rounds snapshot without error, whatever its shape.
 *
 * Everything here is pure — no React, no Supabase, no statistics. Task
 * counts feed prep-request badges only, never file statistics (§21.1).
 */

const DEBUG_TAG = '[prepTasks]';

function safeList(tasks) {
  return Array.isArray(tasks) ? tasks.filter((t) => t && typeof t === 'object') : [];
}

function newId() {
  return crypto.randomUUID();
}

/** A fresh, empty, top-level task row. */
export function newPrepTask(overrides = {}) {
  return { id: newId(), text: '', completed: false, parent: null, ...overrides };
}

/**
 * Read-side resolution of the hierarchy WITHOUT assigning ids or
 * re-ordering: returns a new array whose items carry a valid `parent`
 * (null, or the id of a top-level item present in the array).
 *   - parent missing / unknown / self            → null
 *   - parent is itself a sub-task (depth > 2)    → climbed to the top-level
 *     ancestor (console.debug), cycles → null
 * Items without an id can never be parents.
 */
export function resolveParents(tasks) {
  const list = safeList(tasks);
  const byId = new Map();
  list.forEach((t) => {
    if (typeof t.id === 'string' && t.id && !byId.has(t.id)) byId.set(t.id, t);
  });
  return list.map((t) => {
    const raw = typeof t.parent === 'string' && t.parent ? t.parent : null;
    if (!raw || raw === t.id || !byId.has(raw)) return { ...t, parent: null };
    // Climb past any sub-task parent to the top-level ancestor.
    const seen = new Set([t.id]);
    let cur = byId.get(raw);
    while (cur && !seen.has(cur.id) && typeof cur.parent === 'string' && cur.parent && cur.parent !== cur.id && byId.has(cur.parent)) {
      seen.add(cur.id);
      cur = byId.get(cur.parent);
    }
    if (!cur || seen.has(cur.id)) {
      if (typeof console !== 'undefined' && console.debug) console.debug(DEBUG_TAG, 'parent cycle — task treated as top-level', t.id);
      return { ...t, parent: null };
    }
    if (cur.id !== raw && typeof console !== 'undefined' && console.debug) {
      console.debug(DEBUG_TAG, 'sub-task nested deeper than two levels — re-parented to top-level ancestor', t.id, '→', cur.id);
    }
    return { ...t, parent: cur.id };
  });
}

/** Parent-then-children order; assumes parents are already resolved. */
function orderByHierarchy(resolved) {
  const children = new Map();
  resolved.forEach((t) => {
    if (t.parent) {
      if (!children.has(t.parent)) children.set(t.parent, []);
      children.get(t.parent).push(t);
    }
  });
  const out = [];
  resolved.forEach((t) => {
    if (t.parent) return;
    out.push(t);
    (children.get(t.id) || []).forEach((c) => out.push(c));
  });
  return out;
}

/**
 * THE safety net, run on every save path. Returns a NEW array in which:
 *   - every item has a string id (missing ones assigned, duplicates re-issued),
 *     a string `text`, a boolean `completed` and an explicit `parent`;
 *   - parents are resolved as in resolveParents (unknown → null, depth
 *     capped at two, reported at console.debug);
 *   - the ordering invariant holds (each parent followed by its children).
 * Any other field an item carries is preserved.
 */
export function normalisePrepTasks(tasks) {
  const seenIds = new Set();
  const withIds = safeList(tasks).map((t) => {
    let id = typeof t.id === 'string' && t.id ? t.id : newId();
    if (seenIds.has(id)) id = newId();
    seenIds.add(id);
    return {
      ...t,
      id,
      text: typeof t.text === 'string' ? t.text : '',
      completed: t.completed === true,
    };
  });
  return orderByHierarchy(resolveParents(withIds));
}

/** Top-level tasks only — the "N tasks" count everywhere (§28.5). */
export function countTopLevelTasks(tasks) {
  return resolveParents(tasks).filter((t) => t.parent === null).length;
}

/** Completed top-level tasks — the "X / N done" numerator. */
export function countTopLevelDone(tasks) {
  return resolveParents(tasks).filter((t) => t.parent === null && t.completed === true).length;
}

/** Sub-tasks of `parentId`, in array order (resolved parents). */
export function childrenOf(tasks, parentId) {
  return resolveParents(tasks).filter((t) => t.parent === parentId);
}

function letter(k) {
  return k < 26 ? String.fromCharCode(97 + k) : String(k + 1);
}

/**
 * Derived numbering for display: returns [{ task, label, depth, index }]
 * in parent-then-children order — "1." / "2." for top-level items and
 * "1.a" / "1.b" for their sub-tasks. `index` is the item's position in
 * the returned (ordered) list. Never stored; recomputed on every change.
 */
export function numberPrepTasks(tasks) {
  const ordered = orderByHierarchy(resolveParents(tasks));
  const out = [];
  let n = 0;
  let k = 0;
  ordered.forEach((task, index) => {
    if (task.parent === null) {
      n += 1;
      k = 0;
      out.push({ task, label: `${n}.`, depth: 0, index });
    } else {
      out.push({ task, label: `${n}.${letter(k)}`, depth: 1, index });
      k += 1;
    }
  });
  return out;
}

// ---- Editor operations (pure; each keeps the ordering invariant) ---------
// All assume a list that already satisfies the invariant (the editor's
// state), return a NEW array, and return the SAME array when nothing
// changes so callers can cheaply detect a no-op.

function indexOf(tasks, id) {
  return tasks.findIndex((t) => t.id === id);
}

/** Index just past the contiguous children of the top-level item at `i`. */
function subtreeEnd(tasks, i) {
  const id = tasks[i].id;
  let j = i + 1;
  while (j < tasks.length && tasks[j].parent === id) j += 1;
  return j;
}

/**
 * Enter: a new empty line at the SAME level, directly after the current
 * line. For a top-level task that has sub-tasks, "directly after" means
 * after its last sub-task, so the new top-level line never lands between
 * a parent and its children. Returns { tasks, id } — `id` is the new row.
 */
export function insertTaskAfter(tasks, currentId) {
  const i = indexOf(tasks, currentId);
  if (i < 0) {
    const added = newPrepTask();
    return { tasks: [...tasks, added], id: added.id };
  }
  const cur = tasks[i];
  const added = newPrepTask({ parent: cur.parent || null });
  const at = cur.parent ? i + 1 : subtreeEnd(tasks, i);
  const next = [...tasks.slice(0, at), added, ...tasks.slice(at)];
  return { tasks: next, id: added.id };
}

/** Append a new top-level line at the end. Returns { tasks, id }. */
export function appendTask(tasks) {
  const added = newPrepTask();
  return { tasks: [...tasks, added], id: added.id };
}

/** True when Tab would do something for this row. */
export function canDemoteTask(tasks, id) {
  const i = indexOf(tasks, id);
  if (i <= 0 || tasks[i].parent) return false;
  for (let p = i - 1; p >= 0; p -= 1) if (!tasks[p].parent) return true;
  return false;
}

/**
 * Tab: demote a top-level task to a sub-task of the nearest preceding
 * top-level task. Its own sub-tasks (if any) come along as sub-tasks of
 * that same new parent, so depth stays at two. Because the demoted task
 * directly followed the previous parent's subtree, only `parent` fields
 * change — the array order is already correct. No-op on the first line
 * and on a line that is already a sub-task.
 */
export function demoteTask(tasks, id) {
  if (!canDemoteTask(tasks, id)) return tasks;
  const i = indexOf(tasks, id);
  let p = i - 1;
  while (p >= 0 && tasks[p].parent) p -= 1;
  const newParent = tasks[p].id;
  const end = subtreeEnd(tasks, i);
  return tasks.map((t, idx) => (idx >= i && idx < end ? { ...t, parent: newParent } : t));
}

/** True when Shift+Tab would do something for this row. */
export function canPromoteTask(tasks, id) {
  const i = indexOf(tasks, id);
  return i >= 0 && Boolean(tasks[i].parent);
}

/**
 * Shift+Tab: promote a sub-task to top level, placed immediately after its
 * former parent's last sub-task; the sub-tasks that followed it under the
 * same parent stay with the old parent. No-op on a top-level task.
 */
export function promoteTask(tasks, id) {
  if (!canPromoteTask(tasks, id)) return tasks;
  const i = indexOf(tasks, id);
  const parentId = tasks[i].parent;
  const without = tasks.filter((t) => t.id !== id);
  const pi = indexOf(without, parentId);
  const at = pi < 0 ? without.length : subtreeEnd(without, pi);
  const promoted = { ...tasks[i], parent: null };
  return [...without.slice(0, at), promoted, ...without.slice(at)];
}

/**
 * Remove a task. A top-level task's sub-tasks are PROMOTED in place (they
 * already sit where top-level items would), never deleted.
 */
export function removeTask(tasks, id) {
  const i = indexOf(tasks, id);
  if (i < 0) return tasks;
  return tasks
    .filter((t) => t.id !== id)
    .map((t) => (t.parent === id ? { ...t, parent: null } : t));
}

/**
 * Move a task one step up (-1) or down (+1) among its siblings. A
 * top-level task moves together with its sub-tasks as one block. A
 * sub-task swaps with its adjacent sibling under the same parent.
 * No-op at either end.
 */
export function moveTask(tasks, id, direction) {
  const i = indexOf(tasks, id);
  if (i < 0 || (direction !== -1 && direction !== 1)) return tasks;
  const item = tasks[i];
  if (item.parent) {
    const j = i + direction;
    if (j < 0 || j >= tasks.length || tasks[j].parent !== item.parent) return tasks;
    const next = [...tasks];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  }
  // Top-level: swap whole blocks.
  const blocks = [];
  for (let s = 0; s < tasks.length; ) {
    if (tasks[s].parent) { s += 1; continue; } // cannot happen on a well-formed list
    const e = subtreeEnd(tasks, s);
    blocks.push(tasks.slice(s, e));
    s = e;
  }
  const b = blocks.findIndex((blk) => blk[0].id === id);
  const target = b + direction;
  if (b < 0 || target < 0 || target >= blocks.length) return tasks;
  [blocks[b], blocks[target]] = [blocks[target], blocks[b]];
  return blocks.flat();
}

/**
 * Save-time clean-up: drop rows whose text is blank (after trimming) and
 * trim the rest. Sub-tasks of a dropped parent are promoted in place.
 */
export function dropBlankTasks(tasks) {
  let out = safeList(tasks).map((t) => ({ ...t, text: typeof t.text === 'string' ? t.text.trim() : '' }));
  const blanks = out.filter((t) => t.text.length === 0).map((t) => t.id);
  blanks.forEach((id) => { out = removeTask(out, id); });
  return out;
}
